if (!customElements.get('pi-p-faq-6')) {
  class PIPFAQ6 extends HTMLElement {
    constructor() {
      super();

      this.items = [];
      this.questions = [];

      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
    }

    connectedCallback() {
      this.items = Array.from(
        this.querySelectorAll('.pi-p-faq-6__item')
      );

      if (!this.items.length) return;

      this.openFirst = this.dataset.openFirst === 'true';
      this.closeOthers = this.dataset.closeOthers === 'true';
      this.mobileAccordion =
        this.dataset.mobileAccordion !== 'false';

      this.questions = Array.from(
        this.querySelectorAll('.pi-p-faq-6__question')
      );

      this.questions.forEach((button) => {
        button.addEventListener('click', this.handleClick);
        button.addEventListener('keydown', this.handleKeydown);
      });

      this.initialize();
    }

    disconnectedCallback() {
      this.questions.forEach((button) => {
        button.removeEventListener('click', this.handleClick);
        button.removeEventListener('keydown', this.handleKeydown);
      });
    }

    /* ----------------------------------------
       INITIALIZE
    ---------------------------------------- */

    initialize() {
      let hasOpenItem = false;

      this.items.forEach((item) => {
        if (item.classList.contains('is-open')) {
          hasOpenItem = true;
        }
      });

      /*
        If no item is already open and
        "Open first item" is enabled
      */
      if (!hasOpenItem && this.openFirst && this.items[0]) {
        this.setItemState(
          this.items[0],
          true,
          false
        );
      }

      /*
        Sync all FAQ items with their
        current classes / aria attributes
      */
      this.items.forEach((item) => {
        const isOpen =
          item.classList.contains('is-open');

        this.setItemState(
          item,
          isOpen,
          false
        );
      });
    }

    /* ----------------------------------------
       MOBILE CHECK
    ---------------------------------------- */

    isMobile() {
      return window.matchMedia(
        '(max-width: 749px)'
      ).matches;
    }

    /* ----------------------------------------
       ACCORDION ENABLE CHECK
    ---------------------------------------- */

    canAccordion() {
      /*
        Desktop always supports accordion.

        On mobile it depends on:
        mobile_accordion setting.
      */

      if (!this.isMobile()) {
        return true;
      }

      return this.mobileAccordion;
    }

    /* ----------------------------------------
       CLICK
    ---------------------------------------- */

    handleClick(event) {
      const button = event.currentTarget;

      const item = button.closest(
        '.pi-p-faq-6__item'
      );

      if (!item) return;

      /*
        If mobile accordion is disabled,
        don't toggle on mobile.
      */
      if (!this.canAccordion()) {
        return;
      }

      const isOpen =
        item.classList.contains('is-open');

      /*
        Toggle clicked item
      */
      this.setItemState(
        item,
        !isOpen,
        true
      );

      /*
        Close all other items if enabled
      */
      if (!isOpen && this.closeOthers) {
        this.items.forEach((otherItem) => {
          if (otherItem !== item) {
            this.setItemState(
              otherItem,
              false,
              true
            );
          }
        });
      }
    }

    /* ----------------------------------------
       KEYBOARD NAVIGATION
    ---------------------------------------- */

    handleKeydown(event) {
      const buttons = this.questions;

      const currentIndex =
        buttons.indexOf(event.currentTarget);

      if (currentIndex === -1) return;

      let nextIndex = null;

      /*
        Arrow Down
      */
      if (event.key === 'ArrowDown') {
        nextIndex =
          (currentIndex + 1) %
          buttons.length;
      }

      /*
        Arrow Up
      */
      if (event.key === 'ArrowUp') {
        nextIndex =
          (currentIndex - 1 + buttons.length) %
          buttons.length;
      }

      /*
        Home
      */
      if (event.key === 'Home') {
        nextIndex = 0;
      }

      /*
        End
      */
      if (event.key === 'End') {
        nextIndex =
          buttons.length - 1;
      }

      if (nextIndex !== null) {
        event.preventDefault();

        buttons[nextIndex].focus();
      }
    }

    /* ----------------------------------------
       SET ITEM STATE
    ---------------------------------------- */

    setItemState(item, open, animate = true) {
      const button =
        item.querySelector(
          '.pi-p-faq-6__question'
        );

      const answer =
        item.querySelector(
          '.pi-p-faq-6__answer'
        );

      if (!button || !answer) return;

      /*
        Update classes
      */
      item.classList.toggle(
        'is-open',
        open
      );

      /*
        Update accessibility
      */
      button.setAttribute(
        'aria-expanded',
        String(open)
      );

      /*
        Reduced motion
      */
      const reducedMotion =
        window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches;

      /*
        Instant state
      */
      if (!animate || reducedMotion) {
        answer.hidden = !open;
        answer.style.height = '';

        return;
      }

      /* ------------------------------------
         OPEN
      ------------------------------------ */

      if (open) {
        /*
          Make answer visible
        */
        answer.hidden = false;

        /*
          Start from 0 height
        */
        answer.style.height = '0px';

        /*
          Force browser layout
        */
        answer.offsetHeight;

        /*
          Animate to content height
        */
        const targetHeight =
          answer.scrollHeight;

        requestAnimationFrame(() => {
          answer.style.height =
            `${targetHeight}px`;
        });

        const onOpenEnd = (event) => {
          if (
            event.propertyName !== 'height'
          ) {
            return;
          }

          /*
            Let content have natural height
          */
          answer.style.height = 'auto';

          answer.removeEventListener(
            'transitionend',
            onOpenEnd
          );
        };

        answer.addEventListener(
          'transitionend',
          onOpenEnd
        );
      }

      /* ------------------------------------
         CLOSE
      ------------------------------------ */

      else {
        /*
          Start from current height
        */
        answer.style.height =
          `${answer.scrollHeight}px`;

        /*
          Force layout
        */
        answer.offsetHeight;

        /*
          Animate to zero
        */
        requestAnimationFrame(() => {
          answer.style.height = '0px';
        });

        const onCloseEnd = (event) => {
          if (
            event.propertyName !== 'height'
          ) {
            return;
          }

          /*
            Hide after animation
          */
          answer.hidden = true;

          answer.style.height = '';

          answer.removeEventListener(
            'transitionend',
            onCloseEnd
          );
        };

        answer.addEventListener(
          'transitionend',
          onCloseEnd
        );
      }
    }
  }

  customElements.define(
    'pi-p-faq-6',
    PIPFAQ6
  );
}